import { z } from 'zod';
import { DashboardArchetypeSchema, DashboardDirectionSchema, DashboardSectionLayoutSchema, DashboardSectionSchema, DashboardSpecSchema, WidgetSchema, } from './dashboard.js';
/**
 * iframe 안 인라인 JS 가 SPEC 을 mutate 한 뒤 부모 React 에 `spec.changed` postMessage 를
 * 보내고, 부모는 본 요청으로 서버에 영속화한다.
 *
 * 정책:
 *  - 서버는 client 가 보낸 spec 을 그대로 dashboards.spec 으로 replace (single source of truth
 *    는 iframe 의 SPEC). DashboardSpecSchema 로 검증만 수행.
 *  - 권한: owner 만. viewer 가 보낸 요청은 forbidden.
 *  - fire-and-forget: 클라이언트는 응답을 기다리지 않음. 실패 시 토스트만.
 */
export const EditDashboardSpecRequestSchema = z.object({
    spec: DashboardSpecSchema,
});
/**
 * 단일 widget 의 server-rendered HTML + inline init script 를 받는 RPC.
 * client (iframe inline JS) 가 SPEC mutate 후 duplicate / change_kind 처럼 chart.js
 * 재초기화가 필요한 op 에서 호출. 부모(React) 가 매개해 endpoint 호출 + 응답을 iframe 으로
 * postMessage 전달.
 */
export const WidgetRenderRequestSchema = z.object({
    widget: WidgetSchema,
    si: z.number().int().min(0),
    wi: z.number().int().min(0),
    direction: DashboardDirectionSchema,
    archetype: DashboardArchetypeSchema,
    columnAliases: z.record(z.string(), z.string()).optional(),
});
export const WidgetRenderResponseSchema = z.object({
    html: z.string(),
    script: z.string(),
});
// ───────────────────────────────────────────────────────────────────────────
// dashboard.edit — LLM 부분 편집 op 어휘
//
// LLM 이 기존 대시보드를 **타겟 수정**할 때 사용. 전체 spec 재방출 대신 작은 op 만 emit →
// 토큰 절약. 서버 `applyEditOps(spec, ops)` 가 적용 후 DashboardSpecSchema 로 재검증.
//
// 인덱스 규약: `si` 는 **전역 섹션 인덱스**(tabs 사용 시 탭을 가로질러 0..N), `wi` 는 그 섹션
// 안의 위젯 인덱스. chat 컨텍스트에 주입되는 인덱스 요약의 번호와 1:1 대응.
//
// 추가/교체 op 은 **완성된 widget/section 객체**를 받는다(LLM 이 컬럼·집계까지 직접 지정).
// ───────────────────────────────────────────────────────────────────────────
const SI = z.number().int().min(0).describe('전역 섹션 인덱스 (0부터)');
const WI = z.number().int().min(0).describe('섹션 내 위젯 인덱스 (0부터)');
/** 대시보드 헤더 텍스트 일괄 수정. 빈 문자열은 해당 항목 제거(subtitle/kicker). title 은 비울 수 없음. */
export const DashboardSetMetaOpSchema = z.object({
    op: z.literal('dashboard.set_meta'),
    title: z.string().min(1).max(200).optional(),
    subtitle: z.string().max(200).optional(),
    kicker: z.string().max(60).optional(),
});
/**
 * 섹션 메타(번호/제목/소개문구/INSIGHT/레이아웃) 수정.
 * lede·insight·number 빈 문자열은 제거(number:'' → "01/02" 번호 숨김).
 */
export const SectionSetMetaOpSchema = z.object({
    op: z.literal('section.set_meta'),
    si: SI,
    /** 섹션 번호(예 "01"). 빈 문자열이면 번호 표시 제거. */
    number: z.string().max(8).optional(),
    title: z.string().min(1).max(120).optional(),
    lede: z.string().max(300).optional(),
    insight: z.string().max(600).optional(),
    layout: DashboardSectionLayoutSchema.optional(),
});
/** 새 섹션 추가. `at` 위치에 삽입(생략 시 맨 끝). tabs 사용 대시보드는 마지막 탭에 추가. */
export const SectionAddOpSchema = z.object({
    op: z.literal('section.add'),
    at: SI.optional(),
    section: DashboardSectionSchema,
});
/** 섹션 삭제. */
export const SectionRemoveOpSchema = z.object({
    op: z.literal('section.remove'),
    si: SI,
});
/** 섹션 순서 이동. */
export const SectionMoveOpSchema = z.object({
    op: z.literal('section.move'),
    si: SI,
    dir: z.enum(['up', 'down']),
});
/** 위젯 추가. `wi` 위치에 삽입(생략 시 섹션 맨 끝). 섹션당 최대 8개. */
export const WidgetAddOpSchema = z.object({
    op: z.literal('widget.add'),
    si: SI,
    wi: WI.optional(),
    widget: WidgetSchema,
});
/** 위젯 전체 교체(= 그 자리 위젯을 새 spec 으로). "표 다시 작성" 류에 사용. */
export const WidgetReplaceOpSchema = z.object({
    op: z.literal('widget.replace'),
    si: SI,
    wi: WI,
    widget: WidgetSchema,
});
/** 위젯 삭제. */
export const WidgetRemoveOpSchema = z.object({
    op: z.literal('widget.remove'),
    si: SI,
    wi: WI,
});
/** 위젯 제목만 수정(kpi/comparison/gauge 는 label, 그 외는 title). */
export const WidgetSetTitleOpSchema = z.object({
    op: z.literal('widget.set_title'),
    si: SI,
    wi: WI,
    value: z.string().max(200),
});
export const DashboardEditOpSchema = z.discriminatedUnion('op', [
    DashboardSetMetaOpSchema,
    SectionSetMetaOpSchema,
    SectionAddOpSchema,
    SectionRemoveOpSchema,
    SectionMoveOpSchema,
    WidgetAddOpSchema,
    WidgetReplaceOpSchema,
    WidgetRemoveOpSchema,
    WidgetSetTitleOpSchema,
]);
/**
 * dashboard.edit 툴 입력 — 기존 대시보드에 적용할 op 리스트(1~20).
 * queryId(optional): 대시보드를 **새 저장 쿼리로 재바인딩**할 때만 전달. 현 쿼리에 없는 차원
 * (예: 제품)을 보려면 raw 행 쿼리를 db.save_query 로 저장한 뒤 그 queryId 를 함께 넘긴다 —
 * 새 쿼리에 없는 구 컬럼을 참조하던 위젯은 드롭되므로 ops 로 새 위젯을 정의한다. 생략 시 기존
 * 쿼리 유지(단순 위젯/섹션 편집).
 */
export const DashboardEditRequestSchema = z.object({
    ops: z.array(DashboardEditOpSchema).min(1).max(20),
    queryId: z.string().uuid().optional(),
});
/**
 * 채팅 대시보드 아티팩트의 수동 편집 영속화 요청.
 * iframe 이 mutate 한 full spec + 현재 버전 번호를 부모(ArtifactPanel)가 전달 → 서버가 재렌더 후
 * 그 버전 content + dashboard_specs 를 in-place 갱신(새 버전 안 만듦). owner 만.
 */
export const ArtifactDashboardSpecRequestSchema = z.object({
    version: z.number().int().min(1),
    spec: DashboardSpecSchema,
});
//# sourceMappingURL=dashboard-editops.js.map